import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Skeleton,
  Stack,
  IconButton,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { fetchEventPlayerProps } from '../../services/oddsApi';
import {
  getPropMarketsForSport,
  getDefaultPropMarket,
} from '../../utils/propConstants';
import PlayerPropCard from '../PlayerPropCard';

const PropCardSkeleton = () => (
  <Box sx={{ mb: 1.5, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
      <Skeleton width={160} height={22} />
      <Skeleton width={36} height={22} />
    </Box>
    <Skeleton width="100%" height={80} />
  </Box>
);

const PlayerPropsScreen = () => {
  const { sportKey, eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const event = location.state?.event;

  const propMarkets = getPropMarketsForSport(sportKey);
  const [selectedMarket, setSelectedMarket] = useState(getDefaultPropMarket(sportKey));
  const [propsData, setPropsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Prevents stale responses from overwriting newer results when switching markets quickly
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    const loadProps = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchEventPlayerProps(sportKey, eventId, selectedMarket);

        if (requestId !== requestIdRef.current) return;

        // Group outcomes by player name
        const playerMap = {};

        if (data?.bookmakers) {
          data.bookmakers.forEach((bookmaker) => {
            const market = bookmaker.markets?.find((m) => m.key === selectedMarket);
            if (!market?.outcomes) return;

            market.outcomes.forEach((outcome) => {
              const name = outcome.description || 'Unknown Player';
              if (!playerMap[name]) playerMap[name] = [];
              playerMap[name].push({
                ...outcome,
                bookmaker: bookmaker.key,
                bookmakerTitle: bookmaker.title,
              });
            });
          });
        }

        const playerProps = Object.keys(playerMap)
          .sort()
          .map((playerName) => ({ playerName, outcomes: playerMap[playerName] }));

        setPropsData({
          bookmakers: data?.bookmakers || [],
          playerProps,
        });
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error('Error loading player props:', err);
        setError('Failed to load player props. Please try again.');
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    };

    loadProps();
  }, [sportKey, eventId, selectedMarket]);

  const gameTitle = event
    ? `${event.away_team} @ ${event.home_team}`
    : 'Player Props';

  return (
    <>
      {/* Back + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/player-props'))} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600} noWrap>
          {gameTitle}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5 }}>
        Player Props
      </Typography>

      {/* Market selector */}
      {propMarkets.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1,
            mb: 2,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {propMarkets.map((market) => (
            <Chip
              key={market.key}
              label={market.label}
              clickable
              color={selectedMarket === market.key ? 'secondary' : 'default'}
              variant={selectedMarket === market.key ? 'filled' : 'outlined'}
              onClick={() => setSelectedMarket(market.key)}
              sx={{ flexShrink: 0, fontWeight: selectedMarket === market.key ? 700 : 400 }}
            />
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Stack spacing={0}>
          {[...Array(6)].map((_, i) => (
            <PropCardSkeleton key={i} />
          ))}
        </Stack>
      )}

      {/* Props list */}
      {!loading && !error && propsData && (
        <>
          {propsData.playerProps.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No player props available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try a different prop category or check back closer to game time.
              </Typography>
            </Box>
          ) : (
            propsData.playerProps.map(({ playerName, outcomes }) => (
              <PlayerPropCard
                key={playerName}
                playerName={playerName}
                outcomes={outcomes}
                bookmakers={propsData.bookmakers}
                marketKey={selectedMarket}
              />
            ))
          )}
        </>
      )}
    </>
  );
};

export default PlayerPropsScreen;
